import React from 'react';
import classnames from 'classnames/bind';
import CompactAccordion from '@COMPONENTS/COMMON/accordion/CompactAccordion.component';
import ModernAddButton from '@COMPONENTS/COMMON/buttons/ModernAddButton/ModernAddButton.component';
import styles from './ModernTreeView.module.scss';

const cx: CX = classnames.bind(styles);

interface TreeItem {
    id: number;
    name: string;
    children?: TreeItem[];
}

interface ModernTreeViewProps {
    items: TreeItem[];
    level: 1 | 2 | 3;
    onItemClick: (id: number) => void;
    onAddClick: (chapterId: number, parentId?: number | null) => void;
    addButtonText: string;
    chapterId: number;
    parentId?: number | null;
    canEdit: boolean;
}

function ModernTreeView({
    items,
    level,
    onItemClick,
    onAddClick,
    addButtonText,
    chapterId,
    parentId = null,
    canEdit,
}: ModernTreeViewProps) {
    const handleAddClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onAddClick(chapterId, parentId);
    };

    return (
        <div className={cx('modern-tree-view', `level-${level}`)}>
            {items.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const nextLevel = Math.min(level + 1, 3) as 1 | 2 | 3;

                const actions = (
                    <div className={cx('item-actions')}>
                        {canEdit && level < 3 && (
                            <ModernAddButton
                                title={level === 1 ? 'Add Section' : 'Add Sub-Section'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAddClick(chapterId, item.id);
                                }}
                                size="small"
                                variant="neutral"
                            />
                        )}
                    </div>
                );

                return (
                    <CompactAccordion
                        key={item.id}
                        title={item.name}
                        level={level}
                        onTitleClick={() => onItemClick(item.id)}
                        actions={actions}
                        defaultExpanded={false}
                    >
                        {hasChildren && (
                            <ModernTreeView
                                items={item.children!}
                                level={nextLevel}
                                onItemClick={onItemClick}
                                onAddClick={onAddClick}
                                addButtonText={level === 1 ? 'Add Sub-Section' : 'Add Sub Sub-Section'}
                                chapterId={chapterId}
                                parentId={item.id}
                                canEdit={canEdit}
                            />
                        )}

                        {!hasChildren && canEdit && level < 3 && (
                            <div className={cx('add-section-wrapper')}>
                                <ModernAddButton
                                    title={level === 1 ? 'Add Sub-Section' : 'Add Sub Sub-Section'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddClick(chapterId, item.id);
                                    }}
                                    size="small"
                                    variant="neutral"
                                />
                            </div>
                        )}
                    </CompactAccordion>
                );
            })}

            {canEdit && (
                <div className={cx('add-main-section-wrapper')}>
                    <ModernAddButton
                        title={addButtonText}
                        onClick={handleAddClick}
                        size="small"
                        variant="neutral"
                    />
                </div>
            )}
        </div>
    );
}

ModernTreeView.defaultProps = {
    parentId: null,
};

export default ModernTreeView; 